import Vue from "vue";
import store from "@/store";
import routes from "./routes";
import VueRouter from "vue-router";
import { gerarPermissoesPorCargo } from "../utils/permissoes";
import {
  ROTA_ACOMPANHAMENTO_MENSAL,
  validarRedirecionamentoCampanha
} from "../features/campanha/acompanhamento-mensal/guards/campanhaGuard";

Vue.use(VueRouter);

// Sobrescreve o método push para capturar erros de NavigationDuplicated e de redirecionamento
const originalPush = VueRouter.prototype.push;
VueRouter.prototype.push = function push(location) {
  return originalPush.call(this, location).catch(err => {
    if (
      err.name === "NavigationDuplicated" ||
      (err.message && err.message.includes("Redirected when going from"))
    ) {
      return err;
    }
    throw err;
  });
};

export default Promise.all(routes).then(routes => {
  const router = new VueRouter({
    mode: "history",
    routes
  });

  // Handler global para erros específicos de redirecionamento
  router.onError(error => {
    if (error.message && error.message.includes("Redirected when going from")) {
      // Ignora o erro de redirecionamento
      return;
    }
    console.error(error);
  });

  router.beforeEach((routeTo, routeFrom, next) => {
    const isErp =
      store.state.user?.tipoUsuario === "erp" || store.state.me?.user_erp;
    const token = store.state.access_token || store.state.user || store.state.me;
    const lgpd = store.state.termoLgpd || store.state.me?.HasAceiteDocumentoJuridico === true || store.state.me?.hasAceiteDocumentoJuridico === true;
    const termoAssembleia = store.state.termoAssembleia;
    const assembleia = store.state.validaTermoAssembleia;
    const assinaturaEletronica = store.state.assinaturaEletronica;
    const eSocial = store.state.eSocial;
    const infoBancaria = store.state.infoBancaria;
    const telEmergencia = store.state.telEmergencia;
    const menuItemsRotas = Array.isArray(store.state.menuItemsRotas)
      ? store.state.menuItemsRotas.slice()
      : [];

    const { linksPermitidos } = gerarPermissoesPorCargo(store);
    const rotaSolicitada = routeTo.path;

    const possuiPermissao =
      rotaSolicitada === ROTA_ACOMPANHAMENTO_MENSAL ||
      linksPermitidos.some(
        link => rotaSolicitada === link || rotaSolicitada.startsWith(link + "/")
      );

    // Inclusão manual de rotas no menu (verificando duplicatas)
    if (!menuItemsRotas.find(item => item.link === "/redefinicaoSenha")) {
      menuItemsRotas.push({ link: "/redefinicaoSenha" });
    }
    if (!menuItemsRotas.find(item => item.link === "/termo/lgpd")) {
      menuItemsRotas.push({ link: "/termo/lgpd" });
    }
    if (!menuItemsRotas.find(item => item.link === "/termo/assembleia")) {
      menuItemsRotas.push({ link: "/termo/assembleia" });
    }
    if (!menuItemsRotas.find(item => item.link === "/assinaturaEletronica")) {
      menuItemsRotas.push({ link: "/assinaturaEletronica" });
    }
    if (!menuItemsRotas.find(item => item.link === "/")) {
      menuItemsRotas.push({ link: "/" });
    }
    if (!menuItemsRotas.find(item => item.link === "/login")) {
      menuItemsRotas.push({ link: "/login" });
    }

    store.commit("SET_ROTA", routeTo.fullPath);
    store.commit("SET_MOSTRA_MENU", false);

    // Se a rota requer login e não há token, redireciona para /login
    if (routeTo.meta.requireLogin && !token) {
      return next({ path: "/login" });
    } else if (
      !routeTo.meta.requireLogin &&
      routeTo.fullPath === "/login" &&
      token
    ) {
      return next("/");
    }

    // Se já assinou ou validou os termos, não permite acessar a rota novamente
    if (routeTo.fullPath === "/termo/lgpd" && lgpd) {
      return next("/");
    }
    if (routeTo.fullPath.includes("/termo/assembleia") && assembleia) {
      return next("/");
    }
    if (routeTo.fullPath === "/assinaturaEletronica" && assinaturaEletronica) {
      return next("/");
    }
    if (
      routeTo.fullPath === "/perfilPessoal/regularizacaoInfoBancaria" &&
      infoBancaria
    ) {
      return next("/");
    }
    if (
      routeTo.fullPath === "/perfilPessoal/telefoneEmergencia" &&
      telEmergencia
    ) {
      return next("/");
    }

    // Redireciona para os termos pendentes
    if (!lgpd && token) {
      if (routeTo.fullPath !== "/termo/lgpd") {
        return next("/termo/lgpd");
      } else {
        return next();
      }
    }
    // Se for ERP, ignora os demais redirecionamentos obrigatórios
    if (isErp) {
      return next();
    }
    if (!assembleia && token) {
      const link = "/termo/assembleia/" + termoAssembleia;
      if (!routeTo.fullPath.includes("/termo/assembleia")) {
        return next(link);
      } else {
        return next();
      }
    }

    if (token && !isErp) {
      try {
        const redirecionamento = validarRedirecionamentoCampanha(
          store,
          routeTo
        );
        if (redirecionamento) {
          return next(redirecionamento);
        }
        if (
          store.state.acompanhamentoMensal?.temPendencia &&
          routeTo.path === ROTA_ACOMPANHAMENTO_MENSAL
        ) {
          return next();
        }
      } catch (error) {
        console.error("Erro ao validar redirecionamento da campanha:", error);
      }
    }

    if (!assinaturaEletronica && token) {
      if (routeTo.fullPath !== "/assinaturaEletronica") {
        return next("/assinaturaEletronica");
      } else {
        return next();
      }
    } else if (!eSocial && token) {
      if (routeTo.fullPath !== "/perfilPessoal/regularizacaoEsocial") {
        return next("/perfilPessoal/regularizacaoEsocial");
      } else {
        return next();
      }
    } else if (!infoBancaria && token) {
      if (routeTo.fullPath !== "/perfilPessoal/regularizacaoInfoBancaria") {
        return next("/perfilPessoal/regularizacaoInfoBancaria");
      } else {
        return next();
      }
    } else if (!telEmergencia && token) {
      if (routeTo.fullPath !== "/perfilPessoal/telefoneEmergencia") {
        return next("/perfilPessoal/telefoneEmergencia");
      } else {
        return next();
      }
    }

    if (
      routeTo.fullPath !== "/astec/projetos" &&
      routeTo.fullPath !== "/astec/projetos/novoProjeto"
    ) {
      store.commit("PARAMETROS_FILTRO_PROJETO_ASTEC", null);
    }

    if (routeTo.fullPath !== "/astec/projetos/novoProjeto") {
      if (store.state.projetoAstec) {
        store.commit("PARAMETROS_PROJETO_ASTEC", null);
      }

      if (localStorage.getItem("exibirCamposImportacao") !== null) {
        localStorage.setItem("exibirCamposImportacao", null);
      }
    }

    if (
      routeTo.fullPath !== "/astec/projetos" &&
      routeTo.fullPath !== "/astec/projetos/novoProjeto"
    ) {
      store.commit("PARAMETROS_FILTRO_PROJETO_ASTEC", null);
    }

    if (routeTo.fullPath !== "/astec/projetos/novoProjeto") {
      if (store.state.projetoAstec) {
        store.commit("PARAMETROS_PROJETO_ASTEC", null);
      }

      if (localStorage.getItem("exibirCamposImportacao") !== null) {
        localStorage.setItem("exibirCamposImportacao", null);
      }
    }

    if (!possuiPermissao) {
      console.warn(`⛔️ Acesso negado à rota ${rotaSolicitada}`);
      return next("/");
    }

    return next();
  });

  return router;
});
